import * as Sentry from "@sentry/react";
import { PostgrestError } from "@supabase/supabase-js";
import { isApiError, isNetworkError, toUserError, type UserError } from "@/lib/api-error";

/**
 * Decide se um erro relacionado a chamadas de API deve ir pro Sentry.
 *
 * Vai pro Sentry:
 * - Falhas de rede do browser (`TypeError: Failed to fetch`) APENAS quando o
 *   usuário está online. Offline/instabilidade do lado do cliente é esperado e
 *   só gera ruído (não é bug nosso nem do servidor).
 * - HTTP 5xx — bug do servidor.
 * - Erros do Supabase/Postgrest com código não-esperado.
 * - Erros JS genéricos (TypeError de null.x, etc) — bugs nossos.
 *
 * NÃO vai pro Sentry:
 * - HTTP 4xx (validação, 401/403/404 esperados, conflitos).
 * - PostgREST PGRST116 (no rows) e códigos da família 42xxx (permission/syntax — esperados via RLS).
 */
export function shouldReport(error: unknown): boolean {
  if (isNetworkError(error)) {
    // Só reporta se o browser acha que está online; offline é ruído esperado.
    return typeof navigator !== "undefined" ? navigator.onLine : true;
  }
  if (isApiError(error)) return error.status >= 500;

  if (error && typeof error === "object" && "code" in error) {
    const code = (error as PostgrestError).code;
    if (code === "PGRST116") return false;
    if (typeof code === "string" && code.startsWith("42")) return false;
    return true;
  }

  return error instanceof Error;
}

interface ReportContext {
  /** Origem do erro: "react-query", "manual", ou um nome de feature/módulo. */
  source: string;
  /** Subtipo opcional (ex.: "query" | "mutation"). */
  kind?: string;
  /** Chave da query/mutation (se aplicável) ou identificador da operação. */
  key?: readonly unknown[];
  /** Dados adicionais opcionais (vai para `extra` do Sentry). */
  extra?: Record<string, unknown>;
  /** Tags extras do Sentry, para filtrar o evento. */
  tags?: Record<string, string>;
}

/**
 * Reporta um erro ao Sentry aplicando a triagem 4xx vs 5xx vs rede.
 * Use em catches manuais (fora de useQuery/useMutation) para padronizar a captura.
 */
export function reportApiError(error: unknown, context: ReportContext): void {
  if (!shouldReport(error)) return;
  Sentry.captureException(error, {
    tags: {
      source: context.source,
      ...(context.kind ? { kind: context.kind } : {}),
      ...context.tags,
    },
    extra: {
      key: context.key,
      online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
      ...context.extra,
    },
  });
}

interface UserErrorReportContext extends ReportContext {
  /** O que o usuário tentava fazer, no infinitivo ("renovar o projeto"). Vira o título. */
  action?: string;
}

/**
 * Para catches manuais: traduz o erro para o usuário e reporta ao Sentry o que
 * não é esperado (falha de rede, 5xx, constraint desconhecida, bug de JS).
 * Em `onError` de useMutation use `toUserError`: o MutationCache já reporta.
 */
export function reportUserError(error: unknown, context: UserErrorReportContext): UserError {
  const userError = toUserError(error, { action: context.action });
  if (!userError.expected) reportApiError(error, context);
  else if (userError.reportTags) reportApiError(error, { ...context, tags: { ...context.tags, ...userError.reportTags } });
  return userError;
}

/** Props do toast destrutivo de um catch manual: `toast(userErrorToast(error, { action, source }))`. */
export function userErrorToast(
  error: unknown,
  context: UserErrorReportContext,
): Pick<UserError, "title" | "description"> & { variant: "destructive" } {
  const { title, description } = reportUserError(error, context);
  return { title, description, variant: "destructive" };
}
